/**
 * MegaMenuProducts — hover-triggered mega-menu with 300ms close delay.
 *
 * Each <mega-menu-products> wraps one top-level nav item and its dropdown panel.
 * Hover (desktop) opens the panel; a 300ms timeout on leave prevents accidental
 * closure when the cursor travels from the trigger to the panel.
 * Keyboard: Enter/Space toggles, Escape closes.
 */

if (!customElements.get('mega-menu-products')) {
  class MegaMenuProducts extends HTMLElement {
    constructor() {
      super();
      this.closeDelay = 300;
      this.closeTimeout = null;
      this.trigger = null;
      this.panel = null;
      this.isOpen = false;
    }

    connectedCallback() {
      this.trigger = this.querySelector('.mega-products__trigger');
      this.panel = this.querySelector('.mega-products__panel');
      if (!this.trigger || !this.panel) return;

      // Hover
      this.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
      this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

      // Keyboard
      this.trigger.addEventListener('keydown', this.handleKeydown.bind(this));

      // Click on trigger (for touch devices that fire click)
      this.trigger.addEventListener('click', this.handleClick.bind(this));

      // Close when focus leaves the component
      this.addEventListener('focusout', this.handleFocusOut.bind(this));

      // Notify sticky-header that the menu is open (prevents hide-on-scroll)
      this.headerWrapper = this.closest('.header-wrapper');
    }

    open() {
      if (this.isOpen) return;
      this.closeSiblings();
      this.isOpen = true;
      this.panel.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');

      if (this.headerWrapper) {
        const stickyHeader = this.headerWrapper.closest('sticky-header');
        if (stickyHeader) stickyHeader.preventHide = true;
      }
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.panel.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');

      if (this.headerWrapper) {
        const stickyHeader = this.headerWrapper.closest('sticky-header');
        if (stickyHeader) stickyHeader.preventHide = false;
      }
    }

    closeSiblings() {
      const parent = this.closest('ul');
      if (!parent) return;
      parent.querySelectorAll('mega-menu-products').forEach((sibling) => {
        if (sibling !== this) sibling.close();
      });
    }

    handleMouseEnter() {
      clearTimeout(this.closeTimeout);
      this.open();
    }

    handleMouseLeave() {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = setTimeout(() => this.close(), this.closeDelay);
    }

    handleClick(event) {
      // On desktop (hover-capable), let the link navigate normally
      if (window.matchMedia('(hover: hover)').matches) return;

      // On touch devices, first tap opens the menu; second tap navigates
      if (!this.isOpen) {
        event.preventDefault();
        this.open();
      }
      // If already open, allow default navigation
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        this.trigger.focus();
      }
    }

    handleFocusOut(event) {
      if (!this.contains(event.relatedTarget)) {
        this.close();
      }
    }

    disconnectedCallback() {
      clearTimeout(this.closeTimeout);
    }
  }

  customElements.define('mega-menu-products', MegaMenuProducts);
}
